import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient, getErrorMessage } from '@/lib/api';
import Button from '@/components/ui/Button';
import { AuthLayout, FormError } from '@/components/auth/AuthLayout';
import PasswordField from '@/components/auth/PasswordField';
import { PASSWORD_HINT, strongPassword } from '@/components/auth/password';

const schema = z
  .object({ password: strongPassword, confirmPassword: z.string() })
  .refine((d) => d.password === d.confirmPassword, { message: "The passwords don't match", path: ['confirmPassword'] });
type FormData = z.infer<typeof schema>;

const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) });

  if (!token) {
    return (
      <AuthLayout title="This link is incomplete" subtitle="Open the link from the email again, or ask for a new one.">
        <Link to="/forgot-password" className="text-sm font-medium text-accent-text hover:underline">Send a new reset link</Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password changed" subtitle="You've been signed out everywhere. Sign in with your new password.">
        <Link to="/login" className="inline-flex h-11 items-center rounded-full bg-accent px-6 text-sm font-medium text-[#0B0C0E]">Sign in</Link>
      </AuthLayout>
    );
  }

  const onSubmit = async ({ password }: FormData) => {
    setError(null);
    setBusy(true);
    try {
      await apiClient.post('/auth/password/reset/confirm', { token, password });
      setDone(true);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Choose a new password">
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <PasswordField label="New password" autoComplete="new-password" error={errors.password?.message} helperText={PASSWORD_HINT} {...register('password')} />
        <PasswordField label="Confirm new password" autoComplete="new-password" error={errors.confirmPassword?.message} {...register('confirmPassword')} />
        <FormError message={error} />
        <Button type="submit" size="lg" loading={busy} className="w-full">Change password</Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
